import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/lib/supabase';
import { ErrorDisplay } from '@/components/ui/ErrorDisplay';

interface OrganizationContextType {
  organizationId: string | null;
  organizationName: string | null;
  loading: boolean;
  error: Error | null;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadOrganization() {
      if (!user) {
        if (isMounted) {
          setOrganizationId(null);
          setOrganizationName(null);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // For admin users, don't load organization
        if (user.email?.endsWith('@admin.com')) {
          if (isMounted) {
            setOrganizationId(null);
            setOrganizationName(null);
            setLoading(false);
          }
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('organization_users')
          .select(`
            organizations (
              id,
              name
            )
          `)
          .eq('user_id', user.id)
          .maybeSingle();

        if (fetchError) {
          throw new Error(`Failed to load organization: ${fetchError.message}`, { cause: fetchError });
        }

        if (isMounted) {
          if (data?.organizations) {
            setOrganizationId(data.organizations.id);
            setOrganizationName(data.organizations.name);
          } else {
            setOrganizationId(null);
            setOrganizationName(null);
          }
          setError(null);
        }
      } catch (err) {
        console.error('Error loading organization:', err);
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to load organization data'));
          setOrganizationId(null);
          setOrganizationName(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadOrganization();

    return () => {
      isMounted = false;
    };
  }, [user]);

  return (
    <OrganizationContext.Provider value={{ organizationId, organizationName, loading, error }}>
      {error ? (
        <ErrorDisplay 
          error={error}
          context="Organization Context"
          className="m-4"
        />
      ) : (
        children
      )}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}